import { useLocation } from 'react-router';
import { useNullableTokens } from '../../providers/tokens-provider';
import { useEffect, useMemo, useRef } from 'react';
import Intercom, { shutdown, update } from '@intercom/messenger-js-sdk';
import { INTERCOM_APP_ID } from '@/config';

type IntercomUser = {
    id: string;
    name: string;
    email: string;
    image: string;
};

const isEnabled =
    window.location.hostname === 'portal.safepointapp.com' ||
    window.location.hostname === 'localhost';

export function useIntercom() {
    const hasBooted = useRef<boolean>(false);
    const hasBootedWithUser = useRef<boolean>(false);
    const { tokens } = useNullableTokens();
    const location = useLocation();

    const intercomUser: IntercomUser | null = useMemo(() => {
        return tokens
            ? {
                  id: tokens.accessTokenPayload.user_id,
                  name: `${tokens.accessTokenPayload.user_info.firstName} ${tokens.accessTokenPayload.user_info.lastNames}`,
                  email: tokens.accessTokenPayload.user_info.email,
                  image: tokens.accessTokenPayload.user_info.profilePicture
              }
            : null;
    }, [tokens]);

    useEffect(() => {
        if (!isEnabled) {
            console.log('Intercom disabled on non-production domain');
            return;
        } else {
            console.log('Intercom enabled');
        }
    }, []);

    // Handle Intercom initialization and user state changes
    useEffect(() => {
        if (!isEnabled) {
            return;
        }

        if (!hasBooted.current) {
            // First time initialization
            if (intercomUser) {
                Intercom({
                    app_id: INTERCOM_APP_ID,
                    user_id: intercomUser.id,
                    name: intercomUser.name,
                    email: intercomUser.email,
                    avatar: {
                        type: 'avatar',
                        image_url: intercomUser.image
                    }
                });
                hasBootedWithUser.current = true;
            } else {
                Intercom({
                    app_id: INTERCOM_APP_ID
                });
                hasBootedWithUser.current = false;
            }
            hasBooted.current = true;
        } else {
            // Handle user state changes after initial boot
            if (intercomUser && !hasBootedWithUser.current) {
                // User logged in
                update({
                    user_id: intercomUser.id,
                    name: intercomUser.name,
                    email: intercomUser.email,
                    avatar: {
                        type: 'avatar',
                        image_url: intercomUser.image
                    }
                });
                hasBootedWithUser.current = true;
            } else if (!intercomUser && hasBootedWithUser.current) {
                // User logged out - shutdown and reboot as anonymous
                shutdown();
                Intercom({
                    app_id: INTERCOM_APP_ID
                });
                hasBootedWithUser.current = false;
            }
        }
    }, [intercomUser]);

    // Handle URL changes - ping Intercom when location changes
    useEffect(() => {
        if (!isEnabled) {
            return;
        }

        if (hasBooted.current) {
            update({
                last_request_at: Math.floor(Date.now() / 1000)
            });
        }
    }, [location.pathname, location.search]);
}
